/**
 * Offline Report Service
 * Stores report metadata and file blobs in IndexedDB for offline access.
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
// Không import từ reportFileService: file đó import ngược lại (fetchReportBlob) → vòng.
const REPORT_BUCKET = 'report-files';

const DB_NAME = 'report-vault-offline';
const DB_VERSION = 1;

export interface OfflineReportMeta {
  id: string;
  report_name: string;
  period?: string;
  created_on?: string;
  full_url?: string;
  file_url?: string;
  supabase_file_path?: string;
  file_type?: string;
  file_size?: number;
  saved_at: string;
  summary_by_ai?: string;
  is_summary?: boolean;
  id_group?: string;
  created_by_user_id?: string;
  auth_user_id?: string;
  // Snapshot of reports.updated_at at the time of save — used to detect
  // whether the server has a newer version than the cached copy.
  source_updated_at?: string;
}

interface ReportVaultDB extends DBSchema {
  'reports-meta': {
    key: string;
    value: OfflineReportMeta;
  };
  'report-files': {
    key: string;
    value: Blob;
  };
}

let dbPromise: Promise<IDBPDatabase<ReportVaultDB>> | null = null;

function getDB(): Promise<IDBPDatabase<ReportVaultDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ReportVaultDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('reports-meta')) {
          db.createObjectStore('reports-meta', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('report-files')) {
          db.createObjectStore('report-files');
        }
      },
    });
  }
  return dbPromise;
}

export type OfflineSaveResult =
  | { success: true }
  | { success: false; reason: 'no_downloadable_url' | 'fetch_error' | 'db_error'; message: string };

/**
 * Guess a sensible MIME type from a file name or URL extension.
 * Used as a last-resort fallback when the server response doesn't expose Content-Type.
 */
function guessMimeFromName(name?: string | null): string {
  if (!name) return 'application/octet-stream';
  const lower = name.toLowerCase();
  const ext = lower.slice(lower.lastIndexOf('.') + 1);
  switch (ext) {
    case 'pdf': return 'application/pdf';
    case 'doc': return 'application/msword';
    case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls': return 'application/vnd.ms-excel';
    case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'ppt': return 'application/vnd.ms-powerpoint';
    case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    case 'txt': return 'text/plain';
    case 'csv': return 'text/csv';
    case 'zip': return 'application/zip';
    default: return 'application/octet-stream';
  }
}

/**
 * Ensure a Blob has a usable MIME type. If the type is empty or a generic fallback,
 * re-wrap it using a type inferred from the source URL or file name.
 */
function ensureBlobType(blob: Blob, hint?: string | null): Blob {
  const current = blob.type;
  // These are "not really known" types that cause browsers to display PDFs as text.
  const bad = !current || current === 'text/plain' || current === 'application/octet-stream';
  if (!bad) return blob;
  const guessed = guessMimeFromName(hint);
  if (guessed === current) return blob;
  return new Blob([blob], { type: guessed });
}

/**
 * Fetch file bytes for a report.
 *
 * Strategy:
 *   1. If supabase_file_path exists → tải từ bucket private `report-files` bằng
 *      SDK. Đây là đường của mọi báo cáo tạo từ 2026-08-24 trở đi.
 *   2. Else if file_url (public URL cũ) exists → fetch directly.
 *   3. Else if file_id (Google Drive) exists → invoke the `download-from-drive` edge function,
 *      which uses a service account to bypass Drive's CORS restrictions.
 *   4. Else → return null so the caller can report `no_downloadable_url`.
 */
export async function fetchReportBlob(
  report: {
    id: string;
    report_name?: string | null;
    supabase_file_path?: string | null;
    file_url?: string | null;
    file_id?: string | null;
  },
  onProgress?: (pct: number) => void
): Promise<
  | { ok: true; blob: Blob }
  | { ok: false; reason: 'no_downloadable_url' | 'fetch_error'; message: string }
> {
  // ─── Đường chính: bucket private report-files ───
  // Dùng SDK thay vì fetch thẳng: bucket là private nên URL trần sẽ trả 400,
  // SDK tự đính JWT của người dùng và để RLS quyết định.
  if (report.supabase_file_path) {
    try {
      onProgress?.(10);
      const { data, error } = await supabase.storage
        .from(REPORT_BUCKET)
        .download(report.supabase_file_path);
      if (error || !data) {
        return {
          ok: false,
          reason: 'fetch_error',
          message: error?.message ?? 'Storage không trả về file',
        };
      }
      onProgress?.(50);
      const hint = report.report_name || report.supabase_file_path;
      return { ok: true, blob: ensureBlobType(data, hint) };
    } catch (err) {
      return {
        ok: false,
        reason: 'fetch_error',
        message: err instanceof Error ? err.message : 'Unknown storage error',
      };
    }
  }

  // ─── Đường cũ: public URL đã lưu sẵn ───
  if (report.file_url) {
    try {
      onProgress?.(10);
      const res = await fetch(report.file_url);
      if (!res.ok) {
        return { ok: false, reason: 'fetch_error', message: `HTTP ${res.status} ${res.statusText}` };
      }
      onProgress?.(50);
      const rawBlob = await res.blob();
      // If Storage returned no usable Content-Type, infer from the URL / file name.
      const hint = report.report_name || report.file_url;
      return { ok: true, blob: ensureBlobType(rawBlob, hint) };
    } catch (err) {
      return {
        ok: false,
        reason: 'fetch_error',
        message: err instanceof Error ? err.message : 'Unknown fetch error',
      };
    }
  }

  // ─── Fallback: Google Drive via server-side proxy edge function ───
  // Use a raw fetch (not supabase.functions.invoke) so we keep full control
  // over the response headers — the Content-Type returned by the edge function
  // must survive onto the resulting Blob so Chrome can render PDFs properly.
  if (report.file_id) {
    try {
      onProgress?.(10);
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        return { ok: false, reason: 'fetch_error', message: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.' };
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/download-from-drive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ file_id: report.file_id, report_id: report.id }),
      });

      if (!res.ok) {
        // Error responses from the edge function are JSON.
        let message = `HTTP ${res.status} ${res.statusText}`;
        try {
          const errBody = await res.json();
          if (errBody?.error) message = errBody.error;
        } catch { /* body was not JSON */ }
        return { ok: false, reason: 'fetch_error', message };
      }

      onProgress?.(50);
      const rawBlob = await res.blob();
      // Derive a hint from the edge function's Content-Disposition filename,
      // falling back to the report name if the header isn't exposed.
      let nameHint: string | undefined;
      const disposition = res.headers.get('content-disposition');
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
        if (match) nameHint = decodeURIComponent(match[1]);
      }
      return { ok: true, blob: ensureBlobType(rawBlob, nameHint || report.report_name) };
    } catch (err) {
      return {
        ok: false,
        reason: 'fetch_error',
        message: err instanceof Error ? err.message : 'Unknown fetch error',
      };
    }
  }

  return {
    ok: false,
    reason: 'no_downloadable_url',
    message: 'Báo cáo này không có file đính kèm, không thể lưu offline.',
  };
}

/**
 * Save a report's file blob and metadata to IndexedDB.
 */
export async function saveReportOffline(
  report: {
    id: string;
    report_name?: string | null;
    period?: string | null;
    created_on?: string | null;
    full_url?: string | null;
    file_url?: string | null;
    file_id?: string | null;
    supabase_file_path?: string | null;
    summary_by_ai?: string | null;
    is_summary?: boolean | null;
    id_group?: string | null;
    created_by_user_id?: string | null;
    auth_user_id?: string | null;
    updated_at?: string | null;
  },
  options?: { onProgress?: (pct: number) => void }
): Promise<OfflineSaveResult> {
  const fetchResult = await fetchReportBlob(
    {
      id: report.id,
      report_name: report.report_name,
      supabase_file_path: report.supabase_file_path,
      file_url: report.file_url,
      file_id: report.file_id,
    },
    options?.onProgress
  );

  if (!fetchResult.ok) {
    return { success: false, reason: fetchResult.reason, message: fetchResult.message };
  }

  try {
    const blob = fetchResult.blob;
    options?.onProgress?.(80);

    const db = await getDB();
    const tx = db.transaction(['reports-meta', 'report-files'], 'readwrite');

    const meta: OfflineReportMeta = {
      id: report.id,
      report_name: report.report_name ?? 'Không có tên',
      period: report.period ?? undefined,
      created_on: report.created_on ?? undefined,
      full_url: report.full_url ?? undefined,
      file_url: report.file_url ?? undefined,
      supabase_file_path: report.supabase_file_path ?? undefined,
      file_type: blob.type,
      file_size: blob.size,
      saved_at: new Date().toISOString(),
      summary_by_ai: report.summary_by_ai ?? undefined,
      is_summary: report.is_summary ?? undefined,
      id_group: report.id_group ?? undefined,
      created_by_user_id: report.created_by_user_id ?? undefined,
      auth_user_id: report.auth_user_id ?? undefined,
      source_updated_at: report.updated_at ?? undefined,
    };

    await tx.objectStore('reports-meta').put(meta);
    await tx.objectStore('report-files').put(blob, report.id);
    await tx.done;

    options?.onProgress?.(100);
    return { success: true };
  } catch (err) {
    console.error('[OfflineReport] saveReportOffline error:', err);
    return {
      success: false,
      reason: 'db_error',
      message: err instanceof Error ? err.message : 'Lỗi không xác định',
    };
  }
}

/**
 * Check whether a report is saved offline.
 */
export async function isReportOffline(reportId: string): Promise<boolean> {
  try {
    const db = await getDB();
    const meta = await db.get('reports-meta', reportId);
    return !!meta;
  } catch {
    return false;
  }
}

/**
 * Get all saved offline report metadata.
 */
export async function getOfflineReports(): Promise<OfflineReportMeta[]> {
  try {
    const db = await getDB();
    return await db.getAll('reports-meta');
  } catch {
    return [];
  }
}

/**
 * Open a saved offline report in a new tab via a Blob URL.
 * Returns the Blob URL string (caller must revoke it after use) or null.
 *
 * If the stored Blob has a missing or generic MIME type (common for files
 * saved before the MIME fix landed), re-wrap it with a type inferred from
 * the stored file_type or the report name, so Chrome opens PDFs/docs
 * correctly instead of rendering them as plain text.
 */
export async function openOfflineReport(reportId: string): Promise<string | null> {
  try {
    const db = await getDB();
    const blob = await db.get('report-files', reportId);
    if (!blob) return null;
    const meta = await db.get('reports-meta', reportId);

    // Prefer a valid stored MIME type; otherwise fall back to inferring from the report name.
    const storedMime = meta?.file_type;
    const storedMimeUsable =
      !!storedMime && storedMime !== 'text/plain' && storedMime !== 'application/octet-stream';

    let safeBlob = blob;
    const currentType = blob.type;
    const currentBad =
      !currentType || currentType === 'text/plain' || currentType === 'application/octet-stream';

    if (currentBad) {
      const targetMime = storedMimeUsable ? storedMime : guessMimeFromName(meta?.report_name);
      if (targetMime && targetMime !== currentType) {
        safeBlob = new Blob([blob], { type: targetMime });
      }
    }

    return URL.createObjectURL(safeBlob);
  } catch {
    return null;
  }
}

/**
 * Delete a report from offline storage.
 */
export async function removeOfflineReport(reportId: string): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(['reports-meta', 'report-files'], 'readwrite');
    await tx.objectStore('reports-meta').delete(reportId);
    await tx.objectStore('report-files').delete(reportId);
    await tx.done;
  } catch (err) {
    console.error('[OfflineReport] removeOfflineReport error:', err);
  }
}

/**
 * Estimate total offline storage usage in bytes.
 */
export async function getOfflineStorageUsage(): Promise<number> {
  try {
    const db = await getDB();
    const metas = await db.getAll('reports-meta');
    return metas.reduce((sum, m) => sum + (m.file_size ?? 0), 0);
  } catch {
    return 0;
  }
}

/**
 * Check whether the cached copy of a report is still the latest version.
 *
 * Compares the cached `source_updated_at` against `reports.updated_at` on the
 * server. If the cached meta has no `source_updated_at` (old saves from before
 * this field existed), we also fall back to comparing a handful of content
 * fields so legacy cached reports can still detect changes.
 */
export type FreshnessResult =
  | { status: 'uptodate'; serverUpdatedAt?: string }
  | { status: 'stale'; serverUpdatedAt?: string; reason: 'updated_at' | 'content_changed' | 'unknown' }
  | { status: 'not_cached' }
  | { status: 'error'; message: string };

export async function checkReportFreshness(reportId: string): Promise<FreshnessResult> {
  try {
    const db = await getDB();
    const cached = await db.get('reports-meta', reportId);
    if (!cached) return { status: 'not_cached' };

    // UI dùng report.ID = legacy_id (chuỗi hex 20 ký tự từ SQL Server cũ) làm
    // khoá offline. Đưa thẳng giá trị đó vào cột uuid `id` sẽ nổ
    // "invalid input syntax for type uuid" — phải chọn cột theo dạng khoá.
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(reportId);
    const { data: rows, error } = await supabase
      .from('reports')
      .select('updated_at, report_name, full_url, file_url, summary_by_ai, ai_version_url')
      .eq(isUuid ? 'id' : 'legacy_id', reportId)
      .limit(1);

    if (error) return { status: 'error', message: error.message };
    const data = rows?.[0];
    if (!data) return { status: 'error', message: 'Không tìm thấy báo cáo trên máy chủ.' };

    const serverUpdatedAt = data.updated_at ?? undefined;

    if (cached.source_updated_at && serverUpdatedAt) {
      if (new Date(serverUpdatedAt).getTime() > new Date(cached.source_updated_at).getTime()) {
        return { status: 'stale', serverUpdatedAt, reason: 'updated_at' };
      }
      return { status: 'uptodate', serverUpdatedAt };
    }

    // Legacy fallback: no snapshot of updated_at. Compare a few visible fields.
    const contentChanged =
      (data.report_name ?? '') !== (cached.report_name ?? '') ||
      (data.full_url ?? '') !== (cached.full_url ?? '') ||
      (data.file_url ?? '') !== (cached.file_url ?? '') ||
      (data.summary_by_ai ?? '') !== (cached.summary_by_ai ?? '');

    if (contentChanged) {
      return { status: 'stale', serverUpdatedAt, reason: 'content_changed' };
    }
    // No snapshot + no visible change — assume up-to-date but mark reason so
    // the UI can hint that the check is best-effort.
    return { status: 'uptodate', serverUpdatedAt };
  } catch (err) {
    return {
      status: 'error',
      message: err instanceof Error ? err.message : 'Không thể kiểm tra phiên bản.',
    };
  }
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
